import type { PageGroup, PageGroupType } from '@st/sdk'
import { match, P } from '@st/pattern'
import { US_STATES } from '~/util/us-states'

const states = US_STATES.map(({ code, name }) => ({ value: `US-${code}`, label: name }))

// These are UI states projected from PageGroup, not another persisted model.
type EditorState =
  | { kind: 'unassigned' }
  | { kind: 'no_jurisdiction'; category: PageGroupType }
  | { kind: 'unspecified'; category: PageGroupType }
  | { kind: 'federal'; category: PageGroupType }
  | { kind: 'state'; category: PageGroupType; code: string | null; name: string }
  | { kind: 'local'; category: PageGroupType; name: string }

// A transmittal letter covers every return, and an FBAR authorization belongs to none.
const NO_JURISDICTION_TYPES = ['tax_return_transmittal', 'fbar_authorization'] as const

type Jurisdiction = { level: 'federal' | 'state' | 'local'; code: string | null; name: string }
export type PageGroupEditorAction =
  | { type: 'typeSelected'; value: string }
  | { type: 'stateSelected'; code: string }
  | { type: 'localityChanged'; name: string }

function readEditor(group: PageGroup, types: PageGroupType[]): EditorState {
  const category = types.find((type) => type.id === group.categoryId)
  // Analysis can attach jurisdiction to an uncategorized group. It does not
  // qualify for jurisdiction editing until the user assigns a delivery type.
  return match(category)
    .with(undefined, () => ({ kind: 'unassigned' as const }))
    .with({ id: P.union(...NO_JURISDICTION_TYPES) }, (category) => ({
      kind: 'no_jurisdiction' as const,
      category
    }))
    .otherwise((category) =>
      match((group.metadata.jurisdiction as Jurisdiction | null | undefined) ?? null)
        .with(null, () => ({ kind: 'unspecified' as const, category }))
        .with({ level: 'federal' }, () => ({ kind: 'federal' as const, category }))
        .with({ level: 'state' }, ({ code, name }) => ({
          kind: 'state' as const,
          category,
          code,
          name
        }))
        .with({ level: 'local' }, ({ name }) => ({ kind: 'local' as const, category, name }))
        .exhaustive()
    )
}

// Option values are opaque to the component. The same choices determine both
// the displayed selection and the destination of a typeSelected transition.
function typeOptions(types: PageGroupType[]) {
  const options = types.map((category) => ({
    id: category.id,
    name: category.name,
    choices: match(category.id)
      .with(P.union(...NO_JURISDICTION_TYPES), () => [
        {
          value: category.id,
          label: category.name,
          state: { kind: 'no_jurisdiction', category } satisfies EditorState
        }
      ])
      .otherwise(() =>
        (['federal', 'state', 'local'] as const).map((level) => ({
          value: `${category.id}:${level}`,
          label: `${level[0].toUpperCase()}${level.slice(1)} ${category.name}`,
          state: match(level)
            .returnType<EditorState>()
            .with('federal', () => ({ kind: 'federal', category }))
            .with('state', () => ({ kind: 'state', category, code: null, name: '' }))
            .with('local', () => ({ kind: 'local', category, name: '' }))
            .exhaustive()
        }))
      )
  }))
  const sections = options.filter(({ choices }) => choices.length > 1)
  const otherChoices = options
    .filter(({ choices }) => choices.length === 1)
    .flatMap(({ choices }) => choices)
  if (otherChoices.length > 0) {
    sections.push({ id: 'other', name: 'Other', choices: otherChoices })
  }
  return sections
}

export function selectPageGroupEditor(group: PageGroup, types: PageGroupType[]) {
  const state = readEditor(group, types)
  const options = typeOptions(types)
  const selected = match(state)
    .with({ kind: 'unassigned' }, () => '')
    .with({ kind: 'unspecified' }, ({ category }) => category.id)
    .otherwise(
      (state) =>
        options
          .flatMap((option) => option.choices)
          .find(
            (choice) =>
              choice.state.kind === state.kind &&
              'category' in choice.state &&
              choice.state.category.id === state.category.id
          )!.value
    )
  return {
    canSave: match(state)
      .with({ kind: 'unspecified' }, () => false)
      .with({ kind: 'state' }, ({ code }) => code != null && code !== '')
      .with({ kind: 'local' }, ({ name }) => name.trim().length > 0)
      .with({ kind: P.union('unassigned', 'federal', 'no_jurisdiction') }, () => true)
      .exhaustive(),
    selected,
    options,
    states,
    unqualifiedCategory: match(state)
      .with({ kind: 'unspecified' }, ({ category }) => category)
      .otherwise(() => undefined),
    jurisdiction: match(state)
      .with({ kind: 'state' }, ({ code }) => ({ type: 'state' as const, value: code ?? '' }))
      .with({ kind: 'local' }, ({ name }) => ({ type: 'local' as const, value: name }))
      .otherwise(() => ({ type: 'none' as const }))
  }
}

// Analysis may supply category/jurisdiction without a bookmark title. Saving
// accepts the displayed selection even when no dropdown change event occurred.
export function materializePageGroupEditor(group: PageGroup, types: PageGroupType[]): PageGroup {
  return { ...group, title: editorTitle(readEditor(group, types)) }
}

function editorTitle(state: EditorState): string {
  return match(state)
    .with({ kind: 'unassigned' }, () => '')
    .with({ kind: 'federal' }, ({ category }) => `Federal ${category.name}`)
    .with({ kind: P.union('state', 'local') }, ({ name, category }) =>
      [name, category.name].filter(Boolean).join(' ')
    )
    .with({ kind: P.union('no_jurisdiction', 'unspecified') }, ({ category }) => category.name)
    .exhaustive()
}

/** Any state accepts a type choice. Only State accepts a state selection;
 * only Local accepts locality text. Save/Cancel are owned by the draft container.
 */
export function reducePageGroupEditor(
  group: PageGroup,
  action: PageGroupEditorAction,
  types: PageGroupType[]
): PageGroup {
  const next = match([readEditor(group, types), action] as const)
    .returnType<EditorState>()
    .with([P._, { type: 'typeSelected', value: '' }], () => ({ kind: 'unassigned' }))
    .with(
      [P._, { type: 'typeSelected' }],
      ([, { value }]) =>
        typeOptions(types)
          .flatMap((option) => option.choices)
          .find((choice) => choice.value === value)!.state
    )
    .with([{ kind: 'state' }, { type: 'stateSelected' }], ([state, { code }]) => ({
      ...state,
      code,
      name: states.find((state) => state.value === code)!.label
    }))
    .with([{ kind: 'local' }, { type: 'localityChanged' }], ([state, { name }]) => ({
      ...state,
      name
    }))
    .otherwise(() => {
      throw new Error('Page-group action is not available in the current editor state')
    })

  const jurisdiction = match(next)
    .with({ kind: 'federal' }, () => ({ level: 'federal', code: 'US', name: 'Federal' }))
    .with({ kind: 'state' }, ({ code, name }) => ({ level: 'state', code, name }))
    .with({ kind: 'local' }, ({ name }) => ({ level: 'local', code: null, name }))
    .otherwise(() => null)
  return match(next)
    .with({ kind: 'unassigned' }, () => ({
      ...group,
      categoryId: undefined,
      title: '',
      metadata: {}
    }))
    .otherwise(({ category, kind }) => ({
      ...group,
      categoryId: category.id,
      title: editorTitle(next),
      metadata: match(kind)
        .with('no_jurisdiction', () => ({}))
        .otherwise(() => ({ jurisdiction }))
    }))
}
